import moment from "moment";
import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { createSelector } from "reselect";

import { useAuth } from "@/contexts/auth-context";
import { selectFilteredBookings } from "@/store/selectors";

import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area"
import { ProtectedRoute } from "@/components/protected-route";
import { useDragScroll } from "@/hooks/useDragScroll";
import ReminderBanner from "@/components/reminder-banner";
import { View, SS_Text, SS_TextButton } from "@/components/native-wrapper-ui";
import { HeaderWithNavigation } from "@/components/header-with-navigation";
import NoBookings from "@/components/jobs/no-jobs";

import { getBookings } from "@/store/slices/bookingsSlice";
import { getHSDocuments } from "@/store/actions";
import { updateHelpModal } from "@/store/slices/appStateSlice";

import calendarEmptyIcon from "@/img/calendar-empty.png";
import syncSmIcon from "@/img/sync-sm@3x.png";

// Import missing components (these need to be created or imported from correct location)
import TimesheetSummaryView from "@/components/timesheets/timesheet-card";
import TimesheetDetails from "@/components/timesheets/timesheet-details";
import JobCard from "@/components/jobs/job-card";

// const TimesheetSummaryView = ({ currentWeek, weekEnding, openTimesheetDetails, bookings }) => <div>Timesheet Summary</div>;
// const JobCard = ({ booking, section, navigation }) => <div>Job Overview</div>;

export const Route = createFileRoute("/")({
  component: () => (
    <ProtectedRoute>
      <Page />
    </ProtectedRoute>
  ),
});


function Page() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Redux state
  const dispatch = useDispatch();
  const isOnline = useSelector((state: any) => state.offline.online);
  const retryCount = useSelector((state: any) => state.offline.retryCount);
  const checkins = useSelector((state: any) => state.checkins.items);
  const feedback = useSelector((state: any) => state.feedback.items);
  const bookings = useSelector(selectFilteredBookings);

  const [view, setView] = useState<string>("diary");
  const [oldJob, setOldJob] = useState<false>(false); // update number to an Job type

  const [currentPage, setCurrentPage] = useState(0);
  const [items, setItems] = useState([]);

  // Timesheet modal state
  const [timesheetModalOpen, setTimesheetModalOpen] = useState(false);
  const [selectedTimesheetData, setSelectedTimesheetData] = useState(null);

  // Drag scroll hook for horizontal scrolling
  const attachDragScroll = useDragScroll({ direction: 'horizontal' });

  const updateItems = useCallback(() => {
    const timesheets = [];

    bookings.forEach((booking) => {
      const hasStarted = moment().isAfter(moment(booking.date).startOf("day"));
      const hasEnded = moment().isAfter(moment(booking.date).endOf("day"));

      if (booking.type !== "job" && hasEnded) {
        return;
      }

      let timesheet = timesheets.find(
        (o) => o.id === booking.year + "_" + booking.week_number
      );
      if (!timesheet) {
        timesheets.push({
          id: booking.year + "_" + booking.week_number,
          year: booking.year,
          week_number: booking.week_number,
          week_ending: moment(booking.date).endOf("isoWeek").format("DD MMMM"),
          items: [],
        });
        timesheet = timesheets.find(
          (o) => o.id === booking.year + "_" + booking.week_number
        );
      }

      const checkedinToBooking = checkins.find(
        (checkin) =>
          booking &&
          checkin.booking_id === booking.id &&
          checkin.booking_date === booking.date
      );
      const feedbackOnBooking = feedback.find(
        (feedbackItem) =>
          booking &&
          feedbackItem.booking_id === booking.id &&
          feedbackItem.booking_date === booking.date
      );


      // Create a copy of the booking object to avoid mutating Redux state
      const bookingCopy = { ...booking };

      if (
        checkedinToBooking &&
        checkedinToBooking.checked_out_at !== null &&
        feedbackOnBooking &&
        (feedbackOnBooking.signature ||
          (feedbackOnBooking.feedback == "ho_unavailable" &&
            feedbackOnBooking.reason !== null))
      ) {
        bookingCopy.hide = true;
      } else {
        bookingCopy.hide = false;
      }

      if (hasStarted && !hasEnded) {
        bookingCopy.bookingSection = "Today";
      } else if (hasStarted && hasEnded) {
        bookingCopy.bookingSection = "Overdue";
      } else {
        bookingCopy.bookingSection = "Upcoming";
      }

      timesheet.items.push(bookingCopy);
    });

    setItems(timesheets);
  }, [bookings, checkins, feedback]);

  // refresh data loading
  useEffect(() => {
    if (isOnline) {
      dispatch(getBookings({ page: currentPage }));
    }
  }, [isOnline, currentPage, dispatch]);

  // Process bookings into items
  useEffect(() => {
    updateItems();
  }, [bookings, checkins, feedback, updateItems]);


  return (
    <>
      <HeaderWithNavigation oldJob={oldJob} />

      <div className="flex-1 flex flex-col  bg-gray-50 min-h-[calc(100vh-5rem)]">
      <div className="flex-1 flex flex-col pt-6 px-6">
        <ReminderBanner
          retryCount={retryCount}
          isOnline={isOnline}
          date={
            user && user.lastFetchedBookings
              ? moment(user.lastFetchedBookings, "YYYY-MM-DD hh:mm:ss")
              : null
          }
          refreshDiary={() => {
            setCurrentPage(0);
            dispatch(getBookings({ page: 0 }));
          }}
        />
        </div>

        <ScrollArea ref={attachDragScroll} className="flex-1 h-full items-center">
                <div className="flex flex-1 items-center gap-4 px-6 pb-6 ">

          {!items.length ? (
            <NoBookings
              isOnline={isOnline}
              getBookings={() => {dispatch(getBookings({ page: 0 }))}}
              updateHelpModal={(show) => dispatch(updateHelpModal(show))}
            />
          ) : (
            <div className="flex-1 flex flex-row">
              {items.map((timesheet) => {
                return (
                  <div
                    key={"timesheet-" + timesheet.id}
                    className="flex-1 flex flex-row"
                  >
                    <TimesheetSummaryView
                      currentWeek={timesheet.week}
                      weekEnding={timesheet.week_ending}
                      openTimesheetDetails={() => {
                        setSelectedTimesheetData({
                          weekEnding: timesheet.week_ending,
                          bookings: timesheet.items
                        });
                        setTimesheetModalOpen(true);
                      }}
                      bookings={timesheet.items}
                    />

                    {timesheet.items.map((booking) => {
                      if (booking.hide) {
                        return null;
                      }

                      return (
                        <JobCard
                          key={"booking-" + booking.id + "-" + booking.date}
                          booking={booking}
                          section={booking.bookingSection}
                        />
                      );
                    })}
                  </div>
                );
              })}

              <View className="w-[32rem] h-[40rem] justify-self-end self-end">                
                <View className="flex-1 border border-gray-300 border-dashed">
                  <View className="flex-1 p-6 items-center justify-center">
                    <img
                      className="mb-6"
                      src={calendarEmptyIcon}
                      alt="Empty calendar"
                    />

                    <SS_Text className="text-black text-center">
                      {"Need to see more?"}
                    </SS_Text>
                    <SS_Text className="text-black text-center">
                      {moment()
                        .startOf("isoWeek")
                        .add(currentPage + 2, "weeks")
                        .format("DD MMMM") +
                        " to " +
                        moment()
                          .endOf("isoWeek")
                          .add(currentPage + 2, "weeks")
                          .format("DD MMMM")}
                    </SS_Text>

                    {isOnline && (
                      <SS_TextButton
                        title={"Load Bookings"}
                        linkBtn={true}
                        icon={<img src={syncSmIcon} className="w-4 h-4" />}
                        className="mt-4"
                        onPress={() => {
                          const newPage = currentPage + 1;
                          setCurrentPage(newPage);
                          dispatch(getBookings({ page: newPage }));
                        }}
                      />
                    )}
                  </View>
                </View>
              </View>
            </div>
          )}
                      </div>

                <ScrollBar orientation="horizontal" />

        </ScrollArea>
      </div>

      {/* Timesheet Details Modal */}
      {selectedTimesheetData && (
        <TimesheetDetails
          show={timesheetModalOpen}
          onClose={() => {
            setTimesheetModalOpen(false);
            setSelectedTimesheetData(null);
          }}
          weekEnding={selectedTimesheetData.weekEnding}
          bookings={selectedTimesheetData.bookings}
        />
      )}
    </>
  );
}
